import React, { useState, useRef, useEffect } from "react";
import { FaCheck, FaChevronDown } from "react-icons/fa";

type Option = {
  label: string;
  value: string | number;
};

type CustomSelectProps = {
  options: Option[];
  value: (string | number)[] | null;
  onChange: (value: (string | number)[]) => void;
  placeholder?: string;
  loading?: boolean;
  mode?: "multiple" | undefined;
  id: string;
};

const CustomSelect = ({
  options,
  value,
  onChange,
  placeholder = "Select",
  loading,
  mode,
  id,
}: CustomSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const selectRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      selectRef.current &&
      !selectRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isSelected = (option: Option) => {
    if (!value) return false;
    return value.includes(option.value);
  };

  const handleSelect = (option: Option) => {
    if (mode === "multiple") {
      const currentValues = value || [];
      const isCurrentlySelected = isSelected(option);

      if (isCurrentlySelected) {
        onChange(currentValues.filter((v) => v !== option.value));
      } else {
        onChange([...currentValues, option.value]);
      }
    } else {
      onChange([option.value]);
      setIsOpen(false);
    }
  };
  const selectedOption = options?.find(
    (opt) => opt?.value === (value && value[0])
  );
  const getSelectedLabels = () => {
    if (!value || value.length === 0) return placeholder;
    if (value.length > 1) {
      return `${value.length} selected`;
    }

    return selectedOption ? selectedOption.label : placeholder;
  };

  return (
    <div className="relative !w-[140px]" ref={selectRef}>
      <div
        className="border border-gray-300 rounded-md px-2 w-[140px] text-sm font-dm !h-8 flex items-center justify-between cursor-pointer bg-white"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedOption?.label ? (
          <span className="truncate font-dm">{getSelectedLabels()}</span>
        ) : (
          <span className="truncate  font-dm text-[#cdcdcd]">
            {placeholder}
          </span>
        )}
        <FaChevronDown
          className={`w-3 h-3 transition-transform text-[#cdcdcd] ${
            isOpen ? "transform rotate-180" : ""
          }`}
        />
      </div>

      {isOpen && (
        <div className="vi-scr-glass-popup absolute z-10 w-full mt-1 max-h-60 overflow-auto">
          <input
            type="text"
            className="w-full px-3 py-2 border-b border-gray-300 bg-white text-sm font-dm"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />

          <div className="py-1 ">
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`px-3 py-2 cursor-pointer  flex items-center justify-between hover:bg-gray-100
                  ${isSelected(option) ? "bg-gray-50" : ""}`}
                onClick={() => handleSelect(option)}
              >
                <span className="truncate text-sm font-dm">{option.label}</span>
                {isSelected(option) && (
                  <FaCheck className="w-4 h-4 text-blue-600" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
